import React, { useCallback, useState } from 'react';
import axios from 'axios';
import routes from '../api_routes';

import '../components/VideoSubmissionSection.css'

const CHUNK_SIZE = 10*1024*1024;

const VideoSubmissionSection = ({channelId}) => {
    const [file, setFile] = useState(null);

    const [episodeName, setEpisodeName] = useState('');
    const [description, setDescription] = useState('');
    const [preview, setPreview] = useState('');
    const [isFree, setIsFree] = useState('');
    const [hasAds, setHasAds] = useState('');
    const [length, setLength] = useState('');
    const [filesize, setFilesize] = useState('');
    const [imagePreview, setImagePreview] = useState(null);

    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('');
    const [uploading, setUploading] = useState(false);
    const [finished, setFinished] = useState(false);

    const imgFormats = [".jpg", ".jpeg", ".gif", ".svg", ".png", ".tiff", ".webp", ".bmp"];

    const buttonsDisabled = () => {
        return uploading;
    }

    const fieldsFilled = () => {
        return file !== null && length?.length > 0 && episodeName?.length > 0 && preview !== '';
    }
   
    const isImage = (filename) => {
        if(filename !== null) {
            for(let format of imgFormats) {
                if(filename.endsWith(format) || filename.startsWith("data:image/")) {
                return true;
                }
            }
        }
        return false;
    }

    const handleFileChange = (e) => {
        if(e.target.files?.[0]) {
            setFile(e.target.files[0]);
            setFilesize(`${(e.target.files[0].size / 1000).toFixed(2)}KB`);
        }
    };

    const handleImageChange = (event) => {
        const f = event.target.files;
        if (f !== null && f !== undefined && f.length > 0) {      
            if(isImage(f[0].name)) {
                setPreview(f[0]);
                const reader = new FileReader();
                reader.onload = (e) => {
                setImagePreview(e.target.result);
                };
                reader.readAsDataURL(f[0]);
            }
        }
        else {
            setImagePreview(null);
        }
    }

    const clearFileArea = () => {
        setFile(null);
    }

    const clearImageArea = () => {
        setImagePreview(null);
    }
    
    const handleNameChange = (e) => {
        const t = e.target.value;
        if (t !== null && t !== undefined) {
            setEpisodeName(t);
        }
    };

    const handleDescriptionChange = (e) => {
        const t = e.target.value;
        if (t !== null && t !== undefined) {
            setDescription(t);
        }
    };

    const handleLengthChange = (e) => {
        const t = e.target.value;
        if (t !== null && t !== undefined) {
        const validPattern = /^[0-9:]*$/;
        if(validPattern.test(t)) {
            setLength(t);
        }
        }
    }

    const handleAdsChange = (e) => {
        setHasAds(e.target.checked);
    }

    const handleFreeChange = (e) => {
        setIsFree(e.target.checked);
    }

    const uploadLargeFile = useCallback(async () => {
        if(!file) return;
        const token = localStorage.getItem('submission_token');

        setUploading(true);
        setStatus('Preparing upload...');
        setProgress(0);

        try {
            const totalParts = Math.ceil(file.size / CHUNK_SIZE);

            const {data} = await axios.post(`${routes.upload}/start_upload`, {
                filename: file.name,
                channel_id: channelId,
                content_type: file.type,
                total_parts: totalParts
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            const { upload_id, episode_id, part_key, presigned_urls } = data;

            setFinished(false);
            setStatus('Uploading...');
            const uploadedParts = [];
            let bytesUploaded = 0;

            const uploadChunk = async(index) => {
                const start = index * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                let this_url = presigned_urls[index];

                const response = await axios.put(presigned_urls[index], chunk, {
                    headers: {
                        "Content-Type": false
                    },
                    onUploadProgress: (progressEvent) => {
                        if(progressEvent.loaded) {
                            const currentChunkUploaded = progressEvent.loaded;
                            const runningTotal = bytesUploaded + currentChunkUploaded;
                            setProgress(Math.min(99, Math.round((runningTotal / file.size) * 100)));
                        }
                    }
                });

                const etag = response.headers.etag?.replace(/"/g, '');
                if(!etag) throw new Error(`No ETag for part ${index+1}`);

                uploadedParts.push({
                    PartNumber: index + 1,
                    ETag: etag
                });

                bytesUploaded += end - start;
            };

            const concurrencyLimit = 4;
            for (let i = 0; i < totalParts; i+= concurrencyLimit) {
                const batch = Array.from(
                    { length: Math.min(concurrencyLimit, totalParts-i)},
                    (_, j) => i + j
                );

                await Promise.all(batch.map(uploadChunk));
            }

            setStatus('Finalising...');
            await axios.post(`${routes.upload}/finish_upload`, {
                    channel_id: channelId,
                    episode_id,
                    part_key,
                    upload_id,
                    parts: uploadedParts
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );


            const formData = new FormData();
            formData.append('name', episodeName);
            formData.append('description', description);
            formData.append('placeholder', preview);
            formData.append('isFree', isFree);
            formData.append('hasAds', hasAds);
            formData.append('length', length);
            const metadata_route = `${routes.upload}/${channelId}/${episode_id}/metadata`;
            await axios.post(metadata_route, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": 'multipart/form_data'
            }});

            setFinished(true);
            setProgress(100);
            setStatus('Submission upload complete.');
        }
        catch(err) {
            console.error(err);
            setFinished(true);
            setStatus(`Submission upload failed: ${err} `);
        }
        finally {
            setUploading(false);
        }
    }, [file, preview, description, episodeName, hasAds, isFree, length, channelId]);

    return (
        <div>
            <span className="UploadGroup">
                <div>
                <div> 
                    <span className="HorizontalGroup BottomGap">
                        {
                        file !== null && 
                        <>
                            <p>{file.name} ({filesize})</p>
                            <button className="CancelButton" onClick={clearFileArea} disabled={buttonsDisabled()}>✖</button>
                        </>
                        } 
                        <div>
                            <input type="file" accept="video/*|audio/*" id="video-submission-input" onChange={handleFileChange} disabled={uploading} className="UploadInput" />
                            <label htmlFor="video-submission-input" className="UploadLabel">
                                Select episode file
                            </label>
                            
                        </div>
                    </span>
                    <div className="BottomGap">
                        <label htmlFor={`video-submission-upload-name`} className="FieldLabel">
                            Episode name
                        </label>
                        <input type="text" id={`video-submission-upload-name`} onChange={handleNameChange} className="TextInput"/>
                    </div>
                    <div className="BottomGap">
                        <label htmlFor={`video-submission-upload-description`} className="FieldLabel">
                            Episode description
                        </label>
                        <textarea type="text" cols={40} id={`video-submission-upload-description`} onChange={handleDescriptionChange} className=""/>
                    </div>
                    <div className="BottomGap">
                    <label htmlFor={`video-submission-upload-length`} className="FieldLabel">
                        Episode length (use hh:mm:ss)
                    </label>
                    <input type="text" id={`video-submission-upload-length`} onChange={handleLengthChange} className="TextInput"/>
                    </div>
                    <div className="BottomGap">
                        <input type="checkbox" id={`video-submission-upload-free`} onChange={handleFreeChange} className=""/>
                        <label htmlFor={`video-submission-upload-free`} className="">
                            Is this a free episode?
                        </label>
                    </div>
                    <div className="BottomGap">
                        <input type="checkbox" id={`video-submission-upload-ads`} onChange={handleAdsChange} className=""/>
                        <label htmlFor={`video-submission-upload-ads`} className="">
                            Show ads on this episode?
                        </label>
                    </div>
                    <div className="BottomGap">
                        {   imagePreview && 
                        <>
                        <img src={imagePreview} className="UploadPreviewImage" alt="Preview"/>
                    
                        <span className="HorizontalGroup">
                            <p className="UploadFilename">{preview?.name}</p>
                            <button className="CancelButton" onClick={clearImageArea} disabled={buttonsDisabled()}>✖</button>
                        </span>
                        </>
                        }
                        
                        <input type="file" accept="image/*" id={`video-submission-upload-preview`} onChange={handleImageChange} className="UploadInput"/>
                        <label htmlFor={`video-submission-upload-preview`} className="UploadLabel">
                            Select preview image
                        </label>
                    </div>
                </div>                
                </div>
            </span>


            {file && (
                <>
                <p>Selected: <b>{file.name}</b> ({(file.size / 1024/1024).toFixed(1)} MB)</p>

                <button onClick={uploadLargeFile} disabled={uploading || !file || !fieldsFilled()} className="StandardButton">
                    {uploading ? "Uploading..." : "Submit"}
                </button>

                {(uploading || finished) && (
                    <div>
                        <progress value={progress} max="100" />
                        <p>{progress}% - {status}</p>
                    </div>
                )}
                </>
            )}
        </div>
    );
}

export default VideoSubmissionSection;